import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

const FleetDataContext = createContext({});

// Explicit column lists so the heavy `scunpacked_data` JSONB column (game-file
// dump, ~135 KB/row on ships) never lands in app-side preloads. If something
// later needs the raw scunpacked record for a single row, do a focused
// `supabase.from(tbl).select('scunpacked_data').eq('id', id)` on demand.
export const SHIPS_LIST_COLS = 'id, name, manufacturer, image_url, pledge_url, created_at, source_uuid, source_key, sync_status, last_synced_at, wiki_slug, image_locked';
export const BLUEPRINTS_LIST_COLS = 'id, name, category, family, image_url, created_at, item_group, source_uuid, source_key, sync_status, last_synced_at, wiki_slug, image_locked';

// Supabase caps a single .select() at 1000 rows. The blueprints catalog grew
// past that once we mirrored ship Components/Weapons/Commodities in from
// sc_items, so single-shot SELECTs silently truncate. Page through with
// .range() instead. Exported so other modules can reuse the pattern.
export async function loadAllBlueprints() {
  const pageSize = 1000;
  const out = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('blueprints').select(BLUEPRINTS_LIST_COLS).order('name')
      .range(from, from + pageSize - 1);
    if (error) { console.error('[blueprints] page load failed', error); break; }
    if (!data || data.length === 0) break;
    out.push(...data);
    if (data.length < pageSize) break;
  }
  return out;
}

/**
 * FleetDataProvider — preloads fleet + event data in the background
 * as soon as a user is authenticated. Data is cached globally so
 * navigating to /fleet or /events is instant.
 */
export function FleetDataProvider({ children }) {
  const { user } = useAuth();
  const [ships, setShips] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Start background preload once user is authenticated
  useEffect(() => {
    if (user && !loaded && !loading) {
      loadAllData();
    }
  }, [user, loaded, loading]);

  // Session tracking — create session on login, update on page navigation
  useEffect(() => {
    if (!user) return;
    let sid = sessionId;
    async function startSession() {
      try {
        const { data } = await supabase.from('user_sessions').insert([{
          user_id: user.id,
          started_at: new Date().toISOString(),
          last_active_at: new Date().toISOString(),
          pages: [{ path: window.location.pathname, ts: new Date().toISOString() }],
        }]).select('id').single();
        if (data) { sid = data.id; setSessionId(data.id); }
      } catch (e) { /* table may not exist yet */ }
    }
    startSession();

    // Track page changes
    function trackPage() {
      if (!sid) return;
      const page = { path: window.location.pathname, ts: new Date().toISOString() };
      supabase.rpc('append_session_page', { session_id: sid, page_data: page }).catch(() => {
        // Fallback: simple last_active update if RPC doesn't exist
        supabase.from('user_sessions').update({ last_active_at: new Date().toISOString() }).eq('id', sid).then(() => {});
      });
    }

    // Listen for popstate (back/forward) and periodic heartbeat
    window.addEventListener('popstate', trackPage);
    const heartbeat = setInterval(() => {
      if (sid) {
        supabase.from('user_sessions').update({ last_active_at: new Date().toISOString() }).eq('id', sid).then(() => {});
      }
    }, 60000); // Update every 60s

    return () => {
      window.removeEventListener('popstate', trackPage);
      clearInterval(heartbeat);
    };
  }, [user]);

  async function loadAllData() {
    setLoading(true);
    try {
      const [{ data: shipsData }, { data: profilesData }, { data: teamsData }, { data: eventsData }, { data: vacanciesData }, blueprintsData] = await Promise.all([
        supabase.from('ships').select(SHIPS_LIST_COLS).order('name'),
        supabase.from('profiles').select('*'),
        supabase.from('teams').select('*'),
        supabase.from('events').select('*').order('date'),
        supabase.from('vacancies').select('*'),
        loadAllBlueprints(),
      ]);
      setShips(shipsData || []);
      setProfiles(profilesData || []);
      // Hide archived teams from member-facing views (admin TeamBuilder fetches its own list)
      setTeams((teamsData || []).filter(t => !t.archived));
      setEvents(eventsData || []);
      setVacancies(vacanciesData || []);
      setBlueprints(blueprintsData || []);
      setLoaded(true);
    } catch (err) {
      console.error('Data preload error:', err);
    }
    setLoading(false);
  }

  // Force refresh — call after admin changes
  const refresh = useCallback(async () => {
    setLoaded(false);
    await loadAllData();
  }, []);

  // Refresh just events (lighter weight)
  const refreshEvents = useCallback(async () => {
    const { data } = await supabase.from('events').select('*').order('date');
    setEvents(data || []);
  }, []);

  // Refresh just fleet data
  const refreshFleet = useCallback(async () => {
    const [{ data: shipsData }, { data: profilesData }] = await Promise.all([
      supabase.from('ships').select(SHIPS_LIST_COLS).order('name'),
      supabase.from('profiles').select('*'),
    ]);
    setShips(shipsData || []);
    setProfiles(profilesData || []);
  }, []);

  // Refresh blueprint catalog + profile ownership
  const refreshBlueprints = useCallback(async () => {
    const [blueprintsData, { data: profilesData }] = await Promise.all([
      loadAllBlueprints(),
      supabase.from('profiles').select('*'),
    ]);
    setBlueprints(blueprintsData || []);
    setProfiles(profilesData || []);
  }, []);

  // Refresh teams + vacancies + members
  const refreshTeams = useCallback(async () => {
    const [{ data: teamsData }, { data: profilesData }, { data: vacanciesData }] = await Promise.all([
      supabase.from('teams').select('*'),
      supabase.from('profiles').select('*'),
      supabase.from('vacancies').select('*'),
    ]);
    setTeams((teamsData || []).filter(t => !t.archived));
    setProfiles(profilesData || []);
    setVacancies(vacanciesData || []);
  }, []);

  // Pre-compute owner data once
  const ownerDataCache = useMemo(() => {
    const data = {};
    ships.forEach(s => { data[s.id] = { owners: 0, totalCount: 0 }; });
    profiles.forEach(p => {
      const userShips = Array.isArray(p.ships) ? p.ships : [];
      userShips.forEach(entry => {
        if (typeof entry === 'object' && entry !== null && entry.ship_id && data[entry.ship_id]) {
          data[entry.ship_id].owners += 1;
          data[entry.ship_id].totalCount += (entry.quantity || 1);
        }
      });
    });
    return data;
  }, [ships, profiles]);

  // Pre-compute blueprint owner data — boolean ownership: each entry = 1 owner
  const blueprintOwnerCache = useMemo(() => {
    const data = {};
    blueprints.forEach(b => { data[b.id] = { owners: 0 }; });
    profiles.forEach(p => {
      const userBps = Array.isArray(p.blueprints) ? p.blueprints : [];
      const seen = new Set();
      userBps.forEach(entry => {
        // Accept both legacy { blueprint_id, quantity } and new "uuid" string formats
        const id = typeof entry === 'string' ? entry : (entry && entry.blueprint_id);
        if (id && data[id] && !seen.has(id)) {
          seen.add(id);
          data[id].owners += 1;
        }
      });
    });
    return data;
  }, [blueprints, profiles]);

  // Pre-compute team profile groupings
  const teamProfilesCache = useMemo(() => {
    const cache = {};
    teams.forEach(t => {
      cache[t.name] = profiles.filter(p => {
        const memberTeams = Array.isArray(p.teams) ? p.teams : (p.team ? [p.team] : []);
        return memberTeams.includes(t.name);
      });
    });
    return cache;
  }, [profiles, teams]);

  // Track a page view — call from any component when it mounts
  const trackPageView = useCallback((path) => {
    if (!sessionId) return;
    const page = { path: path || window.location.pathname, ts: new Date().toISOString() };
    supabase.from('user_sessions').update({
      last_active_at: new Date().toISOString(),
    }).eq('id', sessionId).then(() => {});
  }, [sessionId]);

  return (
    <FleetDataContext.Provider value={{
      ships, profiles, teams, events, vacancies, blueprints, loaded, loading,
      ownerDataCache, teamProfilesCache, blueprintOwnerCache,
      refresh, refreshEvents, refreshFleet, refreshTeams, refreshBlueprints,
      trackPageView, sessionId,
    }}>
      {children}</FleetDataContext.Provider>
  );
}

export function useFleetData() {
  return useContext(FleetDataContext);
}
